<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import type { IUpdateSummary } from '@/domain/menu-import/live-update';
  import type { IDraftSummary } from '@/domain/menu-import/menu-draft';
  import type { TPublishSource } from '@/domain/menu-import/publish-dialog';
  import { listJoin } from '@/domain/store-setup/display-names';
  import {
    summaryKeys,
    summaryStatus,
    updateSummaryKeys,
  } from '@/domain/menu-import/workspace-view';

  /**
   * The summary card of the review (plan §4.5): «Encontramos 14 platos en 4
   * categorías · 4 para revisar» (the amber and red dishes, like the «Para
   * revisar» chip; «Su menú tiene…» for a copied or hand-built menu) or
   * «Todo listo para publicar» once nothing blocks, «Revisar uno por uno»
   * while anything is amber or red, and «Ocultar {count} sin precio» while
   * visible dishes miss their price. An update (plan §3.5) says what changes
   * («Leímos 42 platos: 12 con precio nuevo y 3 nuevos»), that the rest of
   * each dish stays, and links to the menu's dishes left out of the photos.
   */
  const props = withDefaults(
    defineProps<{
      summary: IDraftSummary;
      /** Where the dishes came from (`publishSource`): only an AI read «found» them. */
      source: TPublishSource;
      /** The latest read left something out (`isReadIncomplete`: the partial line or a page card shows). */
      incomplete?: boolean;
      /** Update sessions: what publishing changes, with the chips' counts. */
      update?: IUpdateSummary | null;
    }>(),
    { incomplete: false, update: null }
  );
  const emit = defineEmits<{
    (e: 'queue'): void;
    (e: 'hide-unpriced'): void;
    /** «{count} platos no están en las fotos». */
    (e: 'show-others'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;

  const keys = computed(() => summaryKeys(props.source));
  const updateKeys = computed(() => updateSummaryKeys(props.source));
  /** «12 con precio nuevo y 3 nuevos»; null when nothing changes. */
  const updateParts = computed(() => {
    const update = props.update;
    if (!update) return null;
    const parts: string[] = [];
    if (update.priceChanged > 0)
      parts.push(
        String(
          proxy.$tc('menuImport.update.changedPart', update.priceChanged, {
            count: update.priceChanged,
          })
        )
      );
    if (update.added > 0)
      parts.push(
        String(
          proxy.$tc('menuImport.update.addedPart', update.added, {
            count: update.added,
          })
        )
      );
    return parts.length > 0 ? listJoin(parts, proxy.$i18n.locale) : null;
  });
  /**
   * The update sentence: what changes, «todos tienen el mismo precio…» when
   * nothing does and nothing is held, else today's «Encontramos…».
   */
  const updateSentence = computed<'parts' | 'upToDate' | null>(() => {
    const update = props.update;
    if (!update) return null;
    if (updateParts.value !== null) return 'parts';
    return update.heldLeft === 0 ? 'upToDate' : null;
  });
  const status = computed(() => summaryStatus(props.summary, props.incomplete));
  /** «Todo listo» would repeat «todos tienen el mismo precio…»: an up-to-date update says only that. */
  const shownStatus = computed(() =>
    status.value === 'allSet' &&
    (updateSentence.value === 'upToDate' || props.update?.newMenuOpen)
      ? null
      : status.value
  );
  const canQueue = computed(
    () => props.summary.toCheck > 0 || props.summary.openBlockingQuestions > 0
  );
</script>

<template>
  <section class="v2-menu-summary">
    <p class="summary-text">
      <i18n
        v-if="updateSentence === 'parts'"
        :path="updateKeys.sentence"
        tag="span"
      >
        <template #dishes>
          <strong class="summary-dishes">{{
            $tc(updateKeys.dishes, summary.items, {
              count: summary.items,
            })
          }}</strong>
        </template>
        <template #parts>{{ updateParts }}</template>
      </i18n>
      <i18n
        v-else-if="updateSentence === 'upToDate'"
        :path="updateKeys.upToDate"
        tag="span"
      >
        <template #dishes>
          <strong class="summary-dishes">{{
            $tc(updateKeys.dishes, summary.items, {
              count: summary.items,
            })
          }}</strong>
        </template>
      </i18n>
      <i18n v-else :path="keys.sentence" tag="span">
        <template #dishes>
          <strong class="summary-dishes">{{
            $tc(keys.dishes, summary.items, {
              count: summary.items,
            })
          }}</strong>
        </template>
        <template #categories>{{
          $tc('menuImport.review.summaryCategories', summary.categories, {
            count: summary.categories,
          })
        }}</template>
      </i18n>
      <template v-if="shownStatus !== null">
        <span class="summary-separator" aria-hidden="true"> · </span>
        <span v-if="shownStatus === 'toCheck'" class="summary-check">{{
          $t('menuImport.review.toCheck', { count: summary.toCheck })
        }}</span>
        <span v-else class="summary-ready">{{
          $t('menuImport.review.allSet')
        }}</span>
      </template>
    </p>
    <p v-if="update" class="summary-update">
      <span class="update-note">{{ $t('menuImport.update.onlyPrices') }}</span>
      <button
        v-if="update.others > 0"
        type="button"
        class="update-link"
        @click="emit('show-others')"
      >
        {{
          $tc('menuImport.update.othersLink', update.others, {
            count: update.others,
          })
        }}
      </button>
    </p>
    <div v-if="canQueue || summary.unpricedVisible > 0" class="summary-actions">
      <PosUiButton
        v-if="canQueue"
        color="neutral"
        variant="outline"
        @click="emit('queue')"
        >{{ $t('menuImport.review.oneByOne') }}</PosUiButton
      >
      <PosUiButton
        v-if="summary.unpricedVisible > 0"
        color="neutral"
        variant="outline"
        icon="eye-off-outline"
        @click="emit('hide-unpriced')"
        >{{
          $t('menuImport.review.hideUnpriced', {
            count: summary.unpricedVisible,
          })
        }}</PosUiButton
      >
    </div>
  </section>
</template>

<style scoped lang="scss">
  .v2-menu-summary {
    @apply tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-3 tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white tw-px-4 tw-py-3;
    .summary-text {
      @apply tw-m-0 tw-min-w-[12rem] tw-flex-1 tw-text-sm tw-text-neutral-700 md:tw-text-base;
    }
    .summary-dishes {
      @apply tw-font-semibold tw-tabular-nums tw-text-neutral-900;
    }
    .summary-separator {
      @apply tw-text-neutral-400;
    }
    // The status is one phrase («8 para revisar»): it moves to the next line whole.
    .summary-check {
      @apply tw-whitespace-nowrap tw-font-medium tw-text-amber-700;
    }
    .summary-ready {
      @apply tw-whitespace-nowrap tw-font-medium tw-text-emerald-700;
    }
    .summary-actions {
      @apply tw-flex tw-flex-wrap tw-gap-2;
    }
    // Its own row, right under the sentence.
    .summary-update {
      @apply tw-m-0 tw-flex tw-w-full tw-flex-wrap tw-items-center tw-justify-between tw-gap-x-4 tw-gap-y-1 tw-text-[13px] tw-text-neutral-500;
    }
    .update-link {
      @apply tw-min-h-11 tw-cursor-pointer tw-border-0 tw-bg-transparent tw-p-0 tw-text-left tw-text-[13px] tw-font-medium tw-text-neutral-900 tw-underline tw-underline-offset-4 md:tw-min-h-0;
      &:focus-visible {
        @apply tw-rounded tw-outline-none tw-ring-2 tw-ring-orange-500/40;
      }
    }
  }
</style>
